/**
 * useOrderTrace — Sub-PR H (issue #516)
 *
 * Thin TanStack Query wrapper around `GET /api/orders/:shopifyOrderId/trace`.
 * Fetches the full chronological event chain for one Shopify order
 * (webhook ingress → order upsert → fulfillment-job touches →
 * webhook failures → operator review-queue rows → operator refetch
 * events). Per ADR-0012 §Per-order debugging tool, this is the
 * first thing operators reach for when investigating a specific order.
 *
 * Caching strategy:
 *   - `staleTime: 30s` — trace data is debug-only; aggressive
 *     refetching would noise the panel with rerenders while an
 *     operator scans events. 30s is short enough that a fresh
 *     refetch click + 5s frontend cooldown + 1s round-trip will
 *     still surface the new event within the operator's natural
 *     scroll-back motion.
 *   - `refetchOnWindowFocus: false` — same rationale; the panel is
 *     not a live-monitoring surface, and per-tab-focus refires would
 *     duplicate the multi-table JOIN for no operator benefit.
 *
 * 404 (`kind: 'order_not_found'`) handling — R1 P0 fix:
 *   The backend trace route returns HTTP 404 with an error envelope
 *   for an order absent from the local mirror; the hook synthesises the
 *   `{kind: 'order_not_found', shopifyOrderId}` data state.
 *   `src/lib/api.ts::http.get` throws on any 4xx by default
 *   (axios `validateStatus` default), so the 200-only data path
 *   never sees the discriminated envelope. The `queryFn` below
 *   catches `ApiError.status === 404` and synthesises the
 *   data-state shape — preserving the discriminated-union type's
 *   contract the docstring promised. Any other 4xx / 5xx
 *   propagates as a thrown error → `query.isError`.
 *
 * Backend route requires `requireAdmin()`. The trace panel
 * mirror-gates on `isAdmin`, and since #1921 the trace lives in its
 * own rail tab — an inactive tab panel is not mounted at all
 * (`TabsContent`'s `keepMounted` defaults to `false`), so this hook
 * simply does not exist until the tab is active. Between the two,
 * non-admins never fire the query.
 *
 * Parameter is `string` (not `string | undefined`) — the caller is
 * `TraceTimeline` which already receives non-optional
 * `shopifyOrderId`, and the runtime `enabled` gate now defaults to
 * true. R1 P1 cleanup removed the "belt + suspenders" undefined
 * branch.
 */

import { useQuery } from '@tanstack/react-query';
import { ApiError, http } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import type { OrderTraceResponse } from '@/services/orders';

const TRACE_STALE_MS = 30_000;

export function useOrderTrace(shopifyOrderId: string, options?: { enabled?: boolean }) {
  return useQuery<OrderTraceResponse, Error>({
    queryKey: queryKeys.orders.trace(shopifyOrderId),
    queryFn: async () => {
      try {
        return await http.get<OrderTraceResponse>(`/api/orders/${shopifyOrderId}/trace`);
      } catch (err) {
        // Lift the documented 404 envelope back into the data branch
        // so the component renders the friendly `order.trace.notFound`
        // copy instead of the generic `order.trace.errorLoad` red
        // text. Any other thrown error propagates unchanged.
        if (err instanceof ApiError && err.status === 404) {
          return { kind: 'order_not_found', shopifyOrderId };
        }
        throw err;
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: TRACE_STALE_MS,
    refetchOnWindowFocus: false,
    // No per-hook retry option: this query inherits the global
    // `retryOnlyTransient` default on the app QueryClient (#1155 subitem 5),
    // which is where the rule is written down. Restating it here is what
    // made this comment drift — it claimed every 4xx skips retries while
    // 429 and 408 were already exceptions, and it survived #2288 unchanged
    // while the rule gained a second axis. The only thing worth saying
    // locally: the 404 this hook cares about is already demoted to a data
    // state above, so it never reaches the retry predicate at all.
  });
}
